class StorefrontBundleBuilder extends HTMLElement {
  connectedCallback() {
    this.step = 1;
    this.selectedTemplates = new Set();
    this.license = 1;
    this.unitPrice = Number(this.dataset.basePrice || 200);
    this.basePrice = Number(this.dataset.basePrice || 200);
    this.checkoutUrl = this.dataset.checkoutUrl || '#';

    this.templateCards = Array.from(this.querySelectorAll('[data-bundle-template]'));
    this.licenseCards = Array.from(this.querySelectorAll('[data-bundle-license]'));
    this.stepPanels = Array.from(this.querySelectorAll('[data-bundle-step-panel]'));
    this.stepLabels = Array.from(this.querySelectorAll('[data-bundle-step-label]'));
    this.backButtons = Array.from(this.querySelectorAll('[data-bundle-back]'));
    this.nextButtons = Array.from(this.querySelectorAll('[data-bundle-next]'));
    this.reviewButton = this.querySelector('[data-bundle-review]');
    this.checkoutButton = this.querySelector('[data-bundle-checkout]');
    this.summaryLists = Array.from(this.querySelectorAll('[data-bundle-summary-list]'));
    this.priceNodes = Array.from(this.querySelectorAll('[data-bundle-price]'));
    this.licenseNodes = Array.from(this.querySelectorAll('[data-bundle-license-label]'));
    this.totalNodes = Array.from(this.querySelectorAll('[data-bundle-total]'));
    this.rangeSteps = Array.from(this.querySelectorAll('[data-bundle-range-step]'));
    this.upsellTitles = Array.from(this.querySelectorAll('.bundle-builder__upsell-title'));
    this.licenseMiniNodes = Array.from(this.querySelectorAll('[data-bundle-license-mini]'));
    this.errorNodes = Array.from(this.querySelectorAll('[data-bundle-error]'));

    this.templateCards.forEach((card) => {
      card.addEventListener('click', () => this.toggleTemplate(card));
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.toggleTemplate(card);
      });
    });

    this.licenseCards.forEach((card) => {
      card.addEventListener('click', () => this.selectLicense(card));
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.selectLicense(card);
      });
    });

    this.nextButtons.forEach((button) => button.addEventListener('click', () => this.handleTemplateContinue()));
    this.backButtons.forEach((button) => button.addEventListener('click', () => this.goToStep(Number(button.dataset.bundleBack || 1))));
    if (this.reviewButton) this.reviewButton.addEventListener('click', () => this.goToStep(3));
    if (this.checkoutButton) this.checkoutButton.addEventListener('click', () => this.handleCheckout());

    const defaultLicense = this.licenseCards.find((card) => card.dataset.default === 'true') || this.licenseCards[0];
    if (defaultLicense) this.selectLicense(defaultLicense, false);
    this.render();
    this.animateProgress();
  }

  toggleTemplate(card) {
    const id = card.dataset.bundleTemplate;
    if (this.selectedTemplates.has(id)) {
      this.selectedTemplates.delete(id);
      card.dataset.selected = 'false';
      card.setAttribute('aria-pressed', 'false');
    } else {
      this.selectedTemplates.add(id);
      card.dataset.selected = 'true';
      card.setAttribute('aria-pressed', 'true');
    }
    this.dataset.templateError = 'false';
    this.render();
  }

  handleTemplateContinue() {
    if (!this.selectedTemplates.size) {
      this.dataset.templateError = 'true';
      this.errorNodes[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    this.dataset.templateError = 'false';
    this.goToStep(2);
  }

  selectLicense(card, render = true) {
    this.license = Number(card.dataset.bundleLicense || 1);
    this.unitPrice = Number(card.dataset.unitPrice || this.basePrice);
    this.licenseCards.forEach((item) => {
      const active = item === card;
      item.dataset.selected = String(active);
      item.setAttribute('aria-pressed', String(active));
    });
    if (render) this.render();
  }

  goToStep(step) {
    this.step = step;
    this.dataset.currentStep = String(step);
    this.stepPanels.forEach((panel) => {
      panel.hidden = Number(panel.dataset.bundleStepPanel) !== step;
    });
    this.stepLabels.forEach((label) => {
      const current = Number(label.dataset.bundleStepLabel) === step;
      label.dataset.active = String(current);
    });
    this.render();
    this.animateProgress();
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  animateProgress() {
    const activePanel = this.stepPanels.find((panel) => Number(panel.dataset.bundleStepPanel) === this.step);
    const progress = activePanel?.querySelector('.bundle-builder__progress span');
    if (!progress) return;

    const target = this.step === 3 ? '100%' : this.step === 2 ? '66%' : '33%';
    progress.style.width = this.step === 1 ? '0%' : '33%';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        progress.style.width = target;
      });
    });
  }

  getUnitPrice() {
    return this.unitPrice;
  }

  getTotal() {
    return this.getUnitPrice() * this.license;
  }

  getSelectedTemplateCards() {
    return this.templateCards.filter((card) => this.selectedTemplates.has(card.dataset.bundleTemplate));
  }

  render() {
    const selectedCards = this.getSelectedTemplateCards();

    this.summaryLists.forEach((list) => {
      list.replaceChildren();

      if (!selectedCards.length) {
        const empty = document.createElement('span');
        empty.className = 'bundle-builder__summary-empty';
        empty.textContent = 'No templates selected yet.';
        list.append(empty);
        return;
      }

      selectedCards.forEach((card) => {
        const pill = document.createElement('span');
        const oldPrice = document.createElement('s');

        pill.className = 'bundle-builder__summary-pill';
        pill.append(document.createTextNode(card.dataset.title || 'Template'));
        oldPrice.textContent = card.dataset.was || '';
        pill.append(oldPrice);
        list.append(pill);
      });
    });

    const total = this.getTotal();
    const licenseLabel = `${this.license} ${this.license === 1 ? 'License' : 'Licenses'}`;
    this.style.setProperty('--bundle-license', String(this.license));

    this.priceNodes.forEach((node) => {
      node.textContent = this.step === 1 ? 'FREE' : `$${total}`;
    });
    this.licenseNodes.forEach((node) => {
      node.textContent = licenseLabel;
    });
    this.totalNodes.forEach((node) => {
      node.textContent = `$${total}`;
    });
    this.licenseMiniNodes.forEach((node) => {
      node.textContent = `${this.license}x`;
    });

    this.rangeSteps.forEach((step) => {
      const value = Number(step.dataset.bundleRangeStep || 1);
      step.dataset.complete = String(value <= this.license);
      step.dataset.active = String(value === this.license);
    });

    this.upsellTitles.forEach((title) => {
      if (this.license >= 3) {
        title.innerHTML = 'You unlocked <span class="bundle-builder__saving">30% OFF</span>!';
      } else {
        const save = this.license === 2 ? '30%' : '15%';
        title.innerHTML = `Add <strong>1 more</strong> to save <span class="bundle-builder__saving">${save}</span>`;
      }
    });
  }

  handleCheckout() {
    if (!this.checkoutUrl || this.checkoutUrl === '#') return;
    window.location.href = this.checkoutUrl;
  }
}

if (!customElements.get('storefront-bundle-builder')) {
  customElements.define('storefront-bundle-builder', StorefrontBundleBuilder);
}
